import React from "react";
import { Circles } from "react-loader-spinner";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  BoolBadge,
  DetailRow,
  DetailsBlock,
  formatDate,
  PanelCard,
  SectionHeader,
} from "../components/accountUi";
import type { EmployeeProfile } from "../accountTypes";

function formatList(items?: string[] | null) {
  if (!items?.length) return "—";
  const list = items.map((s) => String(s).trim()).filter(Boolean);
  return list.length ? list.join(", ") : "—";
}

function ProfileHero({
  photoUrl,
  initials,
  fullName,
  roleName,
  chips,
}: {
  photoUrl: string | null;
  initials: string;
  fullName: string;
  roleName?: string;
  chips: { label: string; on: boolean }[];
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-4 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl"
        aria-hidden
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center xl:gap-8">
        {photoUrl ? (
          <div className="relative shrink-0">
            <img
              src={photoUrl}
              alt={fullName}
              className="h-28 w-28 rounded-2xl object-cover ring-4 ring-white shadow-lg dark:ring-slate-700"
            />
            <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-800">
              <VerifiedOutlinedIcon sx={{ fontSize: 14 }} />
            </span>
          </div>
        ) : (
          <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primaryLight text-3xl font-bold text-white shadow-lg shadow-primary/25 ring-4 ring-white dark:ring-slate-700">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white capitalize">
            {fullName}
          </p>
          {roleName ? (
            <span className="mt-2 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary dark:bg-primary/20">
              {roleName}
            </span>
          ) : null}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-4 xl:gap-3">
            {chips.map(({ label, on }) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 backdrop-blur-sm dark:border-slate-600/60 dark:bg-slate-900/40"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {label}
                </p>
                <div className="mt-1">
                  <BoolBadge value={on} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

type ProfileTabPanelProps = {
  employee: EmployeeProfile | null;
  loading: boolean;
  fallbackName?: string;
  fallbackRole?: string;
};

export default function ProfileTabPanel({
  employee,
  loading,
  fallbackName,
  fallbackRole,
}: ProfileTabPanelProps) {
  const photoUrl =
    (typeof employee?.imageUrl === "string" && employee.imageUrl) ||
    (typeof employee?.image === "string" && employee.image.startsWith("http")
      ? employee.image
      : null);

  const fullName = employee
    ? [employee.title?.trim(), employee.firstName?.trim(), employee.lastName?.trim()]
        .filter(Boolean)
        .join(" ")
    : fallbackName || "—";

  const roleName = employee?.roleId?.roleName || fallbackRole;
  const initials =
    `${employee?.firstName?.charAt(0) || fallbackName?.charAt(0) || "U"}${(employee?.lastName?.charAt(0) || "").toUpperCase()}`.toUpperCase();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Circles height="44" width="44" color="#174d7a" ariaLabel="loading profile" />
        <p className="text-sm text-slate-500">Loading your profile…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ProfileHero
        photoUrl={photoUrl}
        initials={initials}
        fullName={fullName}
        roleName={roleName}
        chips={[
          { label: "Dentist", on: !!employee?.IsDentist },
          { label: "Book dentist", on: !!employee?.IsBookDentist },
          { label: "Appointment app", on: !!employee?.hasAppointmentApp },
          { label: "Active", on: employee?.status !== false },
        ]}
      />

      <div className="xl:grid xl:grid-cols-12 xl:gap-4 xl:items-start">
        <PanelCard className="p-4 sm:p-5 xl:col-span-5">
          <SectionHeader icon={PersonIcon} title="Contact & credentials" />
          <DetailsBlock>
            <DetailRow label="Employee ID" value={employee?.employeeId} />
            <DetailRow label="Email" value={employee?.email} />
            <DetailRow label="Phone" value={employee?.phoneNumber} />
            <DetailRow label="Address" value={employee?.address} />
          </DetailsBlock>
        </PanelCard>

        <PanelCard className="p-4 sm:p-5 xl:col-span-4">
          <SectionHeader icon={LocalHospitalOutlinedIcon} title="Professional details" />
          <DetailsBlock>
            <DetailRow label="Studies" value={formatList(employee?.studies)} />
            <DetailRow label="Specialities" value={formatList(employee?.specialities)} />
            <DetailRow label="Languages" value={formatList(employee?.languages)} />
            <DetailRow
              label="Assigned treatments"
              value={
                employee?.assignedTreatmentIds?.length
                  ? `${employee.assignedTreatmentIds.length} selected`
                  : undefined
              }
            />
            {employee?.color ? (
              <div className="flex flex-col gap-2 rounded-xl px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Calendar color
                </dt>
                <dd className="flex items-center gap-2 sm:justify-end">
                  <span
                    className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 shadow-inner dark:border-slate-600"
                    style={{ backgroundColor: employee.color }}
                    aria-hidden
                  />
                  <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {employee.color}
                  </span>
                </dd>
              </div>
            ) : null}
          </DetailsBlock>
        </PanelCard>

        <PanelCard className="p-4 sm:p-5 xl:col-span-3">
          <SectionHeader icon={ScheduleOutlinedIcon} title="Account timeline" />
          <DetailsBlock>
            <DetailRow label="Member since" value={formatDate(employee?.createdAt)} />
            <DetailRow label="Last updated" value={formatDate(employee?.updatedAt)} />
          </DetailsBlock>
        </PanelCard>
      </div>
    </div>
  );
}
